import { getCurrentAuthToken, resetCurrentAuthToken } from 'utils/authHelpers';
import { getLocalAppSession } from 'utils/storageHelpers';

/**
 * Signed out in the middle of work (scene 13, D47).
 *
 * The app keeps no copy of the work on the person's own computer (D9), so
 * what was typed after the last "Saved at" is lost when the sign-in runs
 * out. Two things are owed then: the login screen says so, with the time
 * ("You were signed out. Changes since 14:02 were not saved."), and after
 * signing in the person is back on the screen they were on.
 *
 * A screen that stores itself tells this module when the server last kept
 * its work; while such a screen is open the sign-in is looked at every 25
 * seconds, so the app notices within half a minute also while the person
 * types on without a pause (a store is only tried after a pause).
 */
const HOW_OFTEN_TO_LOOK_MS = 25_000;

/** The screens that store themselves and are open now, with their last "Saved at". */
const open = new Map<symbol, Date | null>();
/** The open screens that hold work the server has not confirmed yet. */
const unsaved = new Set<symbol>();
let looking: number | undefined;
let leaving = false;
let checking = false;

const twoDigits = (n: number): string => String(n).padStart(2, '0');

/** The newest "Saved at" among the open screens, as "14:02"; empty when none. */
export const savedAtForNotice = (): string => {
  let newest: Date | null = null;
  open.forEach((at) => {
    if (at && (!newest || at.getTime() > newest.getTime())) newest = at;
  });
  const at = newest as Date | null;
  return at ? `${twoDigits(at.getHours())}:${twoDigits(at.getMinutes())}` : '';
};

/** Does any open screen hold work the server has not confirmed? */
export const unsavedForNotice = (): boolean => unsaved.size > 0;

/** The address of the login screen for someone who was signed out. */
export const signedOutAddress = (since: string, lost = true): string =>
  `/login?redirect=unauthorized${
    since ? `&since=${encodeURIComponent(since)}${lost ? '' : '&kept=1'}` : ''
  }`;

/** The login screen's sentence, in the description's words (5c). */
export const signedOutNotice = (
  since: string | null,
  lost = true
): string | null => {
  if (!since || !/^\d\d:\d\d$/.test(since)) return null;
  // The description's sentence when the app knows of work that was not
  // stored; a softer one when it knows of none (Steven, 5 Oct 2026). It
  // cannot know about words still inside a field.
  return lost
    ? `You were signed out. Changes since ${since} were not saved.`
    : `You were signed out. Everything up to ${since} was saved.`;
};

const askTheServer = (): Promise<Response> => {
  const base = (import.meta.env.VITE_BASE_URL_API as string) ?? '';
  return fetch(`${base}/v4/auth/user/session`, {
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${getCurrentAuthToken('preserve')}`,
      'Active-Customer': String(getLocalAppSession()?.activeCustomer ?? ''),
    },
  });
};

const toTheLoginScreen = (): void => {
  const { pathname, search } = window.location;
  if (leaving || pathname === '/' || pathname.startsWith('/login')) return;
  leaving = true;
  // Read first: what follows changes nothing about it, but the order must
  // never depend on that.
  const since = savedAtForNotice();
  const lost = unsavedForNotice();
  try {
    // The login screen sends the person back to this address (Login.tsx).
    localStorage.setItem('referrer', pathname + search);
  } catch (_e) {
    // Without it the person lands on the start screen, as before.
  }
  // The server no longer takes this sign-in. While the app still held it,
  // the login screen sent the person straight on to an empty dashboard
  // (seen in the browser, 5 Oct 2026).
  resetCurrentAuthToken();
  window.location.replace(signedOutAddress(since, lost));
};

/**
 * A call was answered "not signed in" (401). Some calls answer that for
 * "not allowed" as well (the invoice calls do), so the sign-in itself is
 * asked about first: only when the server refuses THAT too does the person
 * go to the login screen, which says since when changes were not saved and
 * returns here after signing in. Otherwise nothing happens here; the call
 * that was refused reports its own failure.
 */
export const leaveSignedOut = (): void => {
  const { pathname } = window.location;
  if (leaving || checking || pathname === '/' || pathname.startsWith('/login'))
    return;
  checking = true;
  askTheServer()
    .then((response) => {
      if (response.status === 401) toTheLoginScreen();
    })
    // No connection is not "signed out".
    .catch(() => undefined)
    .finally(() => {
      checking = false;
    });
};

const look = (): void => {
  if (document.visibilityState !== 'visible' || leaving || checking) return;
  askTheServer()
    .then((response) => {
      if (response.status === 401) toTheLoginScreen();
    })
    // No connection is not "signed out": the screen's own store says so.
    .catch(() => undefined);
};

/**
 * A screen that stores itself is open. Returns what to call with every new
 * "Saved at", and what to call when the screen closes.
 */
export const watchSignIn = (): {
  kept: (at: Date | null) => void;
  /** Does this screen hold work the server has not confirmed yet? */
  pending: (has: boolean) => void;
  closed: () => void;
} => {
  const screen = Symbol('screen');
  open.set(screen, null);
  // Someone is signed in and working: an older sentence is over, however
  // they signed in.
  dropSignedOutNotice();
  if (looking === undefined) {
    looking = window.setInterval(look, HOW_OFTEN_TO_LOOK_MS);
  }

  return {
    kept: (at) => {
      if (open.has(screen)) open.set(screen, at);
    },
    pending: (has) => {
      if (!open.has(screen)) return;
      if (has) unsaved.add(screen);
      else unsaved.delete(screen);
    },
    closed: () => {
      open.delete(screen);
      unsaved.delete(screen);
      if (open.size === 0 && looking !== undefined) {
        window.clearInterval(looking);
        looking = undefined;
      }
    },
  };
};

/*
 * The login screen tidies its address right after opening and is then drawn
 * afresh, without the time (Login.tsx). The sentence is therefore held for
 * this browser tab until the person has signed in again. It holds a time,
 * nothing of the work (D9).
 */
const HELD = 'signed-out-since';
const HELD_FOR_MS = 15 * 60 * 1000;

export const holdSignedOutNotice = (
  since: string | null,
  lost = true
): void => {
  if (!signedOutNotice(since)) return;
  try {
    sessionStorage.setItem(
      HELD,
      `${since}|${Date.now()}|${lost ? 'lost' : 'kept'}`
    );
  } catch (_e) {
    // Then the sentence is not shown; the "session expired" note still is.
  }
};

export const heldSignedOutNotice = (): string | null => {
  try {
    const [since, at, what] = (sessionStorage.getItem(HELD) ?? '').split('|');
    // Only for the visit to the login screen that follows being signed
    // out: not for whoever opens the login screen in this tab later.
    if (!at || Date.now() - Number(at) > HELD_FOR_MS) return null;
    return signedOutNotice(since, what !== 'kept');
  } catch (_e) {
    return null;
  }
};

export const dropSignedOutNotice = (): void => {
  try {
    sessionStorage.removeItem(HELD);
  } catch (_e) {
    // Nothing was held.
  }
};
